import { EmissionsReportSummaryOrFullT } from '@ruminati/types_v2/emissions-reports'
import {
  type ReportInputKeysT,
  type ReportWithInputsT,
  ReportInputsT,
} from '@ruminati/types_v2/report'

import { VaryingReportTypes } from '@/composables/analytics'

import { CommodityConfig } from './reportsIntensity'

export const reportYears = [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019]
export const reportYearsReversed = JSON.parse(JSON.stringify(reportYears)).reverse()

export const reportYearsAsOptions = convertYearsToOptions(reportYears)

export const futureYears = getNextNYears(reportYears[0], 5)
export const futureYearsAsOptions = convertYearsToOptions(futureYears)

export const allYearsAsOptions = [...reportYearsAsOptions, ...futureYearsAsOptions].sort(
  (a, b) => a.value - b.value,
)

function convertYearsToOptions(years: number[]) {
  return years.map((y) => {
    return {
      label: convertYearToString(y),
      value: y,
    }
  })
}

function getNextNYears(year: number, n: number): number[] {
  const nextYears: number[] = []
  for (let i = 1; i <= n; i++) {
    nextYears.push(year + i)
  }
  return nextYears
}

export function convertYearToString(year: number): string {
  return year.toString()
}

export type SuspiciousResult = {
  commodityKey: string
  issueType: 'too-high' | 'too-low'
}

export function completedReportResultsSeemsSuspicious(
  report: VaryingReportTypes | undefined,
): SuspiciousResult[] | false {
  if (!report?.completionDate || !report.results) return false

  return reportResultsSeemsSuspicious(report)
}

export function reportResultsSeemsSuspicious(
  report: VaryingReportTypes,
): SuspiciousResult[] | false {
  const isSuspicious = [] as SuspiciousResult[]

  if (!report.results) return isSuspicious

  const isGafReport = report.isGafReport

  CommodityConfig.forEach((commodity) => {
    if (!commodity.intensityReference) return true
    const tolerance = commodity.intensityReference.suspiciousTolerance ?? 2
    let intensityValue: undefined | number = undefined

    if (!('cropIntensity' in commodity)) {
      intensityValue = commodity.getCommodityIntensity(report)
    } else {
      if ('resultsByProduct' in report && report.resultsByProduct?.grains) {
        report.resultsByProduct.grains.forEach((c) => {
          if (commodity.isCrop(c) && c.intensity) {
            intensityValue = c.intensity
          }
        })
      } else if (report.results && 'commodities' in report.results) {
        report.results.commodities.cropping?.byCrop.forEach((c) => {
          if (commodity.isCrop(c) && c.intensity.excludingSequestration.kgCo2ePerKgCropProduced) {
            intensityValue = c.intensity.excludingSequestration.kgCo2ePerKgCropProduced
          }
        })
      }
    }

    if (
      intensityValue &&
      (intensityValue > commodity.intensityReference.nationalAverage * tolerance ||
        intensityValue < commodity.intensityReference.nationalAverage / tolerance)
    ) {
      isSuspicious.push({
        commodityKey: commodity.value,
        issueType:
          intensityValue > commodity.intensityReference.nationalAverage * tolerance
            ? 'too-high'
            : 'too-low',
      })
    }
  })
  if (isGafReport && 'net' in report.results.totals) {
    if (report.results.totals.net && report.results.totals.net < 0) {
      isSuspicious.push({
        commodityKey: 'N/A',
        issueType: 'too-low',
      })
    } else if (report.results.totals.net && report.results.totals.net > 50000000) {
      isSuspicious.push({
        commodityKey: 'N/A',
        issueType: 'too-high',
      })
    }
  }

  return isSuspicious.length > 0 ? isSuspicious : false
}

export function getReportTotalEmissions(
  reportResults: EmissionsReportSummaryOrFullT['results'],
): undefined | number {
  if (!reportResults) return undefined
  if ('total' in reportResults.totals && typeof reportResults.totals.total === 'number')
    return reportResults.totals.total
  if ('totalTco2e' in reportResults.totals) return reportResults.totals.totalTco2e
  return undefined
}

export function getReportTotalSequestration(
  reportResults: EmissionsReportSummaryOrFullT['results'],
): undefined | number {
  if (!reportResults) return undefined
  if ('total' in reportResults.totals && typeof reportResults.totals.sequestration === 'number')
    return reportResults.totals.sequestration
  return undefined
}

type ExtractProperty<T, K extends PropertyKey> = T extends any
  ? K extends keyof T
    ? T[K]
    : never
  : never

export function getReportInputsByKey<
  K extends ReportInputKeysT,
  F = ExtractProperty<ReportInputsT, K>,
>(report: ReportWithInputsT, key: K): F | undefined {
  if (report && report.inputs && key in report.inputs) {
    return report.inputs[key as keyof (typeof report)['inputs']] as F
  }
  return undefined
}
